/*
 * What the search results page and the header search share: the search's settings, the shop
 * context, the search client, formatting, and the live product lookup from Shopify
 * (lookUpProducts). Published as window.awSearch, only on a store that has a Meilisearch index.
 *
 * Reads the shop's settings the layout puts in window.awSearchSettings.
 */
(function () {
  "use strict";

  var settings = window.awSearchSettings || {};

  // A failure the search survives, or that turns it off: written to the browser's console, and
  // reported where the page reports errors (snippets/error-reporting.liquid). The report carries the
  // message and where it happened, never what the visitor searched for.
  var _reportError = function (message, error, context) {
    if (error === undefined) console.error(message);
    else console.error(message, error);
    if (window.awReportError) {
      window.awReportError(new Error(message, { cause: error }), context);
    }
  };

  // The stores that have a Meilisearch index. The key may only search its one index, which is why
  // it can sit in a page every visitor loads. The hosts are separate copies of the index, asked in
  // order (_meilisearchClient).
  var MEILISEARCH_STORES = {
    "autorenwelt-dev-will2.myshopify.com": {
      hosts: ["https://meili-test.autorenwelt.de"],
      searchKey: "f8290dbf0f30c2fde229b657f89a3392321a8fb27ed799d3d0923e89af0956d2",
      index: "dev_products",
    },
    "autorenwelt.myshopify.com": {
      hosts: ["https://search-1.autorenwelt.de", "https://search-2.autorenwelt.de"],
      searchKey: "7ca3c8f82936f689cd0d8b4d4a46007cf169c1ae4d4ac982dbc9d1c9383b4fb0",
      index: "products",
    },
  };
  var meilisearch = MEILISEARCH_STORES[settings.shopDomain];
  // Without a client the header search and the results page stay as the page drew them.
  if (!meilisearch) {
    console.error("Search: " + settings.shopDomain + " has no search index; the search is off");
    return;
  }
  if (!window.instantMeiliSearch) {
    _reportError("Search: the Meilisearch adapter did not load; the search is off");
    return;
  }

  // What the header search and the results page show. Each facet must be one the hosts' index can
  // filter on (filterableAttributes in search-sync/src/storefront.ts): Meilisearch refuses a whole
  // search that asks for any other.
  var SEARCH_CONFIG = {
    input_selector:
      'form[action="/search"] input[type="text"], form[action="/search"] input[type="search"]',
    results_selector: ".algolia-shopify-instantsearch",
    products_autocomplete_hits_per_page: 8,
    products_full_results_hits_per_page: 18,
    facets: [
      { name: "named_tags.onix_contrib", title: "Autoren", type: "conjunctive" },
      { name: "named_tags.publisher", title: "Verlage", type: "conjunctive" },
      { name: "named_tags.keywords", title: "Schlagwörter", type: "conjunctive" },
    ],
  };

  // A Storefront API version Shopify still supports; Shopify keeps each for about a year, then
  // answers the oldest supported one in its place.
  var STOREFRONT_API_VERSION = "2026-10";

  // The collection that lists what a secondary market sells, per store.
  var MARKET_COLLECTION_IDS = {
    "autorenwelt-dev-will2.myshopify.com": "287088803936",
  };
  var DEFAULT_MARKET_COLLECTION_ID = "661184446789";
  var marketCollectionId =
    MARKET_COLLECTION_IDS[settings.shopDomain] || DEFAULT_MARKET_COLLECTION_ID;

  var marketMatch = window.location.pathname.match(/^\/([a-z]{2}-[a-z]{2})\//);
  var market = marketMatch ? marketMatch[1] : null;
  // A German-language market other than the primary one: /de-at/, /de-ch/, ...
  var secondaryMarket = market && market.indexOf("de-") === 0 ? market : null;

  var _escape = function (value) {
    return String(value === undefined || value === null ? "" : value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  };

  var _groupThousands = function (integer, separator) {
    return integer.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
  };

  // Shopify's money format notation, as the store's money_format setting uses it. The format is the
  // shop's own markup and is inserted as HTML.
  var _formatMoney = function (amount, format) {
    var cents = Math.round(Number(amount) * 100);
    var _amount = function (decimals, thousands, decimal) {
      var fixed = (cents / 100).toFixed(decimals).split(".");
      return _groupThousands(fixed[0], thousands) + (fixed[1] ? decimal + fixed[1] : "");
    };
    return String(format || "{{amount}} €").replace(
      /\{\{\s*(\w+)\s*\}\}/,
      function (placeholder, name) {
        switch (name) {
          case "amount":
            return _amount(2, ",", ".");
          case "amount_no_decimals":
            return _amount(0, ",", ".");
          case "amount_with_comma_separator":
            return _amount(2, ".", ",");
          case "amount_no_decimals_with_comma_separator":
            return _amount(0, ".", ",");
          case "amount_with_space_separator":
            return _amount(2, " ", ",");
          case "amount_no_decimals_with_space_separator":
            return _amount(0, " ", ",");
          case "amount_with_apostrophe_separator":
            return _amount(2, "'", ".");
          default:
            return _amount(2, ",", ".");
        }
      },
    );
  };

  var marketContext = secondaryMarket
    ? " @inContext(country: " + secondaryMarket.split("-")[1].toUpperCase() + ", language: DE)"
    : "";

  // Asks Shopify, as the visitor's market sees it, about the products a search returned: whether each
  // still exists, whether it can be ordered, its cover and its price. The search records can lag
  // behind the shop; this answer cannot.
  //
  // Answers { productId: { available, image, price, variants } } with an entry for
  // every product Shopify returned, or null when the request failed. A product without an entry no
  // longer exists, or is not sold in the visitor's market. variants holds { price, compareAtPrice }
  // by variant id, for the formats a search record links to.
  var _lookUpProducts = function (productIds) {
    var query =
      "query getProducts($ids: [ID!]!)" +
      marketContext +
      " { nodes(ids: $ids) { ... on Product { id availableForSale featuredImage { url }" +
      " priceRange { minVariantPrice { amount } }" +
      " variants(first: 10) { edges { node { id price { amount } compareAtPrice { amount } } } } } } }";
    return fetch("/api/" + STOREFRONT_API_VERSION + "/graphql.json", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        query: query,
        variables: {
          ids: productIds.map(function (id) {
            return "gid://shopify/Product/" + id;
          }),
        },
      }),
    })
      .then(function (response) {
        if (!response.ok) throw new Error("Storefront API answered " + response.status);
        return response.json();
      })
      .then(function (body) {
        if (body.errors || !body.data || !Array.isArray(body.data.nodes)) return null;
        var products = {};
        body.data.nodes.forEach(function (product) {
          if (!product || !product.id || !product.priceRange) return;
          var variants = {};
          ((product.variants && product.variants.edges) || []).forEach(function (edge) {
            var variant = edge.node;
            if (variant.id && variant.price) {
              variants[variant.id.replace("gid://shopify/ProductVariant/", "")] = {
                price: parseFloat(variant.price.amount),
                compareAtPrice: variant.compareAtPrice
                  ? parseFloat(variant.compareAtPrice.amount)
                  : 0,
              };
            }
          });
          products[product.id.replace("gid://shopify/Product/", "")] = {
            available: product.availableForSale === true,
            image: product.featuredImage ? product.featuredImage.url : null,
            price: parseFloat(product.priceRange.minVariantPrice.amount),
            variants: variants,
          };
        });
        return products;
      })
      .catch(function () {
        return null;
      });
  };

  // The ids of the products a secondary market sells, read once per page from its collection as
  // that market sees it, or null when Shopify could not be asked. Read live rather than kept in
  // the Meilisearch records: the collection is kept by hand and by tools, apart from the products'
  // tags, so no tag rule could say which records belong to it.
  var marketProductIds = null;
  var _marketProductIds = function () {
    if (marketProductIds) return marketProductIds;
    var query =
      "query marketProducts($id: ID!, $after: String)" +
      marketContext +
      " { collection(id: $id) { products(first: 250, after: $after)" +
      " { nodes { id } pageInfo { hasNextPage endCursor } } } }";
    var _page = function (after, ids) {
      return fetch("/api/" + STOREFRONT_API_VERSION + "/graphql.json", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: query,
          variables: { id: "gid://shopify/Collection/" + marketCollectionId, after: after },
        }),
      })
        .then(function (response) {
          if (!response.ok) throw new Error("Storefront API answered " + response.status);
          return response.json();
        })
        .then(function (body) {
          var collection = body.data && body.data.collection;
          if (body.errors || !collection) throw new Error("no market collection in the answer");
          var products = collection.products;
          products.nodes.forEach(function (product) {
            ids.push(product.id.replace("gid://shopify/Product/", ""));
          });
          return products.pageInfo.hasNextPage ? _page(products.pageInfo.endCursor, ids) : ids;
        });
    };
    marketProductIds = _page(null, []).catch(function (error) {
      // Unnarrowed, the market still finds its books; the live lookup removes the ones it does not sell.
      _reportError("Search: the market's products could not be read; searching them all", error, {
        search_market: secondaryMarket,
      });
      return null;
    });
    return marketProductIds;
  };

  // In a secondary market, every request is narrowed to the products the market sells.
  var _inMarket = function (requests) {
    if (!secondaryMarket) return Promise.resolve(requests);
    return _marketProductIds().then(function (ids) {
      if (ids === null) return requests;
      var filter = "id IN [" + ids.join(", ") + "]";
      return requests.map(function (request) {
        var params = Object.assign({}, request.params);
        params.filters = params.filters ? "(" + params.filters + ") AND " + filter : filter;
        return Object.assign({}, request, { params: params });
      });
    });
  };

  // How long a host may take to answer before the next one is asked as well.
  var HOST_TIMEOUT_MS = 2500;

  // Words as Meilisearch counts them: it ignores punctuation and splits at hyphens, so "?" is a
  // search without words and "harry-potter" one of two; an accent typed as a separate mark stays
  // part of its word.
  var _words = function (query) {
    return (String(query || "").match(/[\p{L}\p{M}\p{N}]+/gu) || []).length;
  };

  // The relevance tags, as a sort Meilisearch applies before exactness (the index's settings,
  // search-sync/src/storefront.ts). Exactness favours a field that equals or starts with the
  // query; unsorted, "harry potter" lists books titled just "Harry Potter" before the series, and
  // "krimi" lists "Krimi-Dinner" puzzle books before the bestselling crime novels.
  var SALES_RANK_SORT = ":named_tags.relevance:desc,named_tags.rel_trend:desc";

  var _sortedBySalesRank = function (request) {
    if (_words(request.params && request.params.query) === 0) return request;
    return Object.assign({}, request, { indexName: request.indexName + SALES_RANK_SORT });
  };

  // Algolia favours a one-word search's exact matches only where a whole field is the word, as
  // the title "Krimi" or the publisher "Diogenes". Sorted, those would sink, so the books among
  // the unsorted first EXACT_CANDIDATES whose field is the word come first on the first page, in
  // their unsorted order, and are left out of the later pages. The first page is longer by those
  // the sorted page did not already hold.
  var EXACT_CANDIDATES = 10;
  // How long the sorted results wait for them; shorter than a host's time limit, so a slow answer
  // here does not send the page to the next host.
  var EXACT_WAIT_MS = 1500;

  var _wantsExactFirst = function (request) {
    var params = request.params || {};
    return _words(params.query) === 1 && params.hitsPerPage !== 0;
  };

  // The hits are shown as they come, highlighted; the facet counts come from the sorted request.
  var _exactCandidates = function (request) {
    var params = Object.assign({}, request.params, { hitsPerPage: EXACT_CANDIDATES, page: 0 });
    delete params.facets;
    delete params.maxValuesPerFacet;
    return Object.assign({}, request, { params: params });
  };

  var _isExact = function (hit) {
    var details = hit._rankingScoreDetails;
    return Boolean(details && details.exactness && details.exactness.matchType === "exactMatch");
  };

  // One hit per product, but not always the same edition in both answers.
  var _withExactFirst = function (result, candidates) {
    var exact = candidates.hits.filter(_isExact);
    if (exact.length === 0) return result;
    var exactIds = {};
    exact.forEach(function (hit) {
      exactIds[hit.id] = true;
    });
    var rest = result.hits.filter(function (hit) {
      return !exactIds[hit.id];
    });
    return Object.assign({}, result, { hits: result.page === 0 ? exact.concat(rest) : rest });
  };

  // Before a client's first search the adapter asks for the whole index's facet counts, without
  // words, which it uses only to list facet values that have no hits (its keepZeroFacets, off
  // here). Asked with pages, that request makes Meilisearch count every record exactly, over a
  // second ahead of the visitor's first search. It carries only the index, the facets and the
  // paging; every search the pages send also asks for highlights.
  var FACET_PRIMER_KEYS = ["indexUid", "facets", "hitsPerPage", "page", "limit", "offset"];

  var _isFacetPrimer = function (query) {
    return Object.keys(query).every(function (key) {
      return FACET_PRIMER_KEYS.indexOf(key) !== -1;
    });
  };

  // The adapter's requests, with the facet counts above answered here, empty, without asking the
  // host. Meilisearch says which kind of exact match a hit is only when asked for its ranking
  // details, which the adapter has no setting for; withRankingDetails asks for them.
  var _httpClient = function (withRankingDetails) {
    return function (url, init) {
      var body = init && typeof init.body === "string" ? JSON.parse(init.body) : null;
      var queries = body && Array.isArray(body.queries) ? body.queries : null;
      if (queries && queries.length > 0 && queries.every(_isFacetPrimer)) {
        return Promise.resolve({
          results: queries.map(function (query) {
            return { indexUid: query.indexUid, hits: [], facetDistribution: {} };
          }),
        });
      }
      if (withRankingDetails && queries) {
        queries.forEach(function (query) {
          query.showRankingScoreDetails = true;
        });
        init = Object.assign({}, init, { body: JSON.stringify(body) });
      }
      return fetch(url, init).then(function (response) {
        return response.text().then(function (text) {
          if (!response.ok) throw new Error("Meilisearch answered " + response.status);
          return text ? JSON.parse(text) : undefined;
        });
      });
    };
  };

  // Without words the count is Meilisearch's estimate: an exact count of one hit per product
  // over the whole catalogue takes over a second per request, and the results page sends several
  // at once. The estimate counts every edition, so it runs a little high, and the adapter then
  // knows only whether a next page exists; the estimate gives the pages beyond it.
  var _withEstimatedPages = function (response) {
    response.results.forEach(function (result) {
      if (result.hitsPerPage && result.nbPages > result.page + 1) {
        result.nbPages = Math.max(result.nbPages, Math.ceil(result.nbHits / result.hitsPerPage));
      }
    });
    return response;
  };

  // Only what the results page and the header search render (search_results.js,
  // search_autocomplete.js): every field and a highlighted copy of every field cost Meilisearch
  // several times the time and bytes. A field they start to read is added here.
  var RESULT_FIELDS = [
    "id",
    "objectID",
    "handle",
    "title",
    "sku",
    "price",
    "compare_at_price",
    "image",
    "variants_count",
    "inventory_quantity",
    "inventory_policy",
    "product_type",
    "named_tags.prod-version",
    "named_tags.wholesaler_note",
    "named_tags.format",
    "named_tags.publisher",
    "named_tags.onix_contrib",
    "named_tags.onix_contrib_author",
    "named_tags.onix_contrib_translator",
  ];
  var HIGHLIGHTED_FIELDS = [
    "title",
    "sku",
    "product_type",
    "named_tags.publisher",
    "named_tags.onix_contrib",
    "named_tags.onix_contrib_author",
    "named_tags.onix_contrib_translator",
  ];

  // Meilisearch counts a request's facets one after another, and on the results page that counting
  // is most of the wait. A request's facets go out beside the search in up to FACET_REQUESTS
  // requests of their own, so they are counted at the same time, and are answered as one. Every
  // request counts against the search hosts' limit per visitor (meilisearch-gate.conf.template) and
  // one refused request fails the search, so the facets are grouped, and the requests that need no
  // splitting travel together in one call, as before.
  var FACET_REQUESTS = 3;

  var _facetGroups = function (facets) {
    var groups = [];
    facets.forEach(function (facet, position) {
      var group = position % FACET_REQUESTS;
      groups[group] = (groups[group] || []).concat(facet);
    });
    return groups;
  };

  var _facetsApart = function (client) {
    var _search = function (requests) {
      var together = [];
      var apart = [];
      var plan = requests.map(function (request) {
        var params = request.params || {};
        var facets = Array.isArray(params.facets) ? params.facets : [];
        if (facets.length < 2) {
          together.push(request);
          return { hits: together.length - 1, facets: [] };
        }
        together.push(
          Object.assign({}, request, { params: Object.assign({}, params, { facets: [] }) }),
        );
        return {
          hits: together.length - 1,
          facets: _facetGroups(facets).map(function (group) {
            var groupParams = Object.assign({}, params, { facets: group, hitsPerPage: 0 });
            apart.push(Object.assign({}, request, { params: groupParams }));
            return apart.length - 1;
          }),
        };
      });
      if (apart.length === 0) return client.search(requests);
      var calls = [client.search(together)].concat(
        apart.map(function (part) {
          return client.search([part]);
        }),
      );
      return Promise.all(calls).then(function (answers) {
        var response = answers[0];
        return Object.assign({}, response, {
          results: plan.map(function (entry) {
            var result = response.results[entry.hits];
            if (entry.facets.length === 0) return result;
            var facets = {};
            var stats = {};
            entry.facets.forEach(function (index) {
              var counted = answers[index + 1].results[0];
              Object.assign(facets, counted.facets);
              Object.assign(stats, counted.facets_stats);
            });
            return Object.assign({}, result, { facets: facets, facets_stats: stats });
          }),
        });
      });
    };
    return { search: _search, clearCache: client.clearCache };
  };

  // Every word must match. A search with a query that finds nothing is run again with words left
  // out, which the results page asks for with removeWordsIfNoResults; the Meilisearch adapter does
  // not pass that on.
  //
  // The store's hosts hold the same index. A search goes to the current host. When that host
  // fails, or has not answered in time, the next host is asked as well, the first answer is
  // used and the page stays with the host that gave it: a host that is only slow keeps the page
  // as long as its answer still comes first.
  var _meilisearchClient = function (store) {
    var _client = function (host, finitePagination, matchingStrategy, withRankingDetails) {
      return _facetsApart(
        window.instantMeiliSearch(host, store.searchKey, {
          finitePagination: finitePagination,
          meiliSearchParams: {
            matchingStrategy: matchingStrategy,
            attributesToRetrieve: RESULT_FIELDS,
            attributesToHighlight: HIGHLIGHTED_FIELDS,
          },
          httpClient: _httpClient(withRankingDetails),
        }).searchClient,
      );
    };
    var hosts = store.hosts.map(function (host) {
      return {
        host: host,
        everyWord: _client(host, true, "all", false),
        fewerWords: _client(host, true, "frequency", false),
        noWords: _client(host, false, "all", false),
        exactCandidates: _client(host, false, "all", true),
      };
    });
    var current = 0;

    var _searchOn = function (index, requests) {
      var host = hosts[index];
      var withWords = requests.some(function (request) {
        return _words(request.params && request.params.query) > 0;
      });
      if (!withWords) return host.noWords.search(requests).then(_withEstimatedPages);
      var sorted = requests.map(_sortedBySalesRank);
      var found = host.everyWord.search(sorted).then(function (response) {
        var nothingFound = requests.some(function (request, position) {
          var query = request.params && request.params.query;
          return query && response.results[position].nbHits === 0;
        });
        return nothingFound ? host.fewerWords.search(sorted) : response;
      });
      var exactFirst = requests.map(_wantsExactFirst);
      var candidates = requests.filter(_wantsExactFirst).map(_exactCandidates);
      if (candidates.length === 0) return found;
      // Without them the results are still sorted, so they cannot fail or hold up the search.
      var exact = Promise.race([
        host.exactCandidates.search(candidates).catch(function (error) {
          _reportError("Search: " + host.host + " did not say which matches are exact", error, {
            search_host: host.host,
          });
          return null;
        }),
        new Promise(function (resolve) {
          setTimeout(function () {
            resolve(null);
          }, EXACT_WAIT_MS);
        }),
      ]);
      return Promise.all([found, exact]).then(function (answers) {
        var response = answers[0];
        if (!answers[1]) return response;
        var next = 0;
        var results = response.results.map(function (result, position) {
          return exactFirst[position]
            ? _withExactFirst(result, answers[1].results[next++])
            : result;
        });
        return Object.assign({}, response, { results: results });
      });
    };

    var _search = function (requests) {
      var first = current;
      var second = first + 1 < hosts.length ? first + 1 : null;
      return new Promise(function (resolve, reject) {
        var done = false;
        var open = 0;
        var secondAsked = false;
        var timer = null;
        var _ask = function (index) {
          open += 1;
          _searchOn(index, requests).then(
            function (response) {
              if (done) return;
              done = true;
              clearTimeout(timer);
              if (index !== first) {
                _reportError(
                  "Search: " +
                    hosts[first].host +
                    " failed or was slow; staying on " +
                    hosts[index].host,
                  undefined,
                  { search_host: hosts[first].host },
                );
                current = index;
              }
              resolve(response);
            },
            function (error) {
              open -= 1;
              if (done) return;
              _reportError("Search: " + hosts[index].host + " failed", error, {
                search_host: hosts[index].host,
              });
              if (index === first && second !== null && !secondAsked) _askSecond();
              else if (open === 0) {
                done = true;
                reject(error);
              }
            },
          );
        };
        var _askSecond = function () {
          if (secondAsked) return;
          secondAsked = true;
          clearTimeout(timer);
          _ask(second);
        };
        _ask(first);
        if (second !== null) timer = setTimeout(_askSecond, HOST_TIMEOUT_MS);
      });
    };

    return {
      search: function (requests) {
        return _inMarket(requests).then(_search);
      },
      clearCache: function () {
        hosts.forEach(function (host) {
          host.everyWord.clearCache();
          host.fewerWords.clearCache();
          host.noWords.clearCache();
          host.exactCandidates.clearCache();
        });
      },
    };
  };

  window.awSearch = {
    config: SEARCH_CONFIG,
    productsIndex: meilisearch.index,
    secondaryMarket: secondaryMarket,
    // Links stay inside the market the visitor is in; the client narrows each request to the
    // market's products itself (_inMarket).
    urlPrefix: secondaryMarket ? "/" + secondaryMarket : "",

    createSearchClient: function () {
      return _meilisearchClient(meilisearch);
    },

    // Whether the number of results for this query is an estimate (_withEstimatedPages).
    countIsEstimated: function (query) {
      return _words(query) === 0;
    },

    lookUpProducts: _lookUpProducts,

    escape: _escape,

    formatMoney: _formatMoney,

    price: function (amount) {
      return _formatMoney(amount, settings.moneyFormat);
    },

    germanNumber: function (value) {
      return Number(value).toLocaleString("de-DE");
    },

    sizedImage: function (image, size) {
      if (!image) return "https://cdn.shopify.com/s/images/admin/no-image-compact.gif";
      return image.replace(/\/(.*)\.(\w{2,4})/g, "/$1_" + size + ".$2");
    },

    // An ISBN-13 typed with hyphens is searched without them; anything else is searched as typed.
    normalizeQuery: function (query) {
      var withoutHyphens = String(query || "").replace(/-/g, "");
      return /^\d{13}$/.test(withoutHyphens) ? withoutHyphens : query;
    },

    on: function (root, type, selector, handler) {
      root.addEventListener(type, function (event) {
        var target = event.target.closest(selector);
        if (target && root.contains(target)) handler(event, target);
      });
    },
  };
})();
